import { Network } from '@capacitor/network';
import { defineStore } from 'pinia'
import { useFeedbackStore } from './feedback';
import { useWebportalStore } from './webportal';
import VLApi from '@/api/VLApi';

//Import data models
import { ScanItem } from '@/models/ScanItem';
import { ScanCounter } from '@/models/ScanCounter';
import { ScanBundel } from '@/models/ScanBundel';
import { ScanPosition } from '@/models/ScanPosition';
import { ScanStation } from '@/models/ScanStation';
import { ScanOverzicht } from '@/models/ScanOverzicht';
import { ScanMultipleItem } from '@/models/ScanMultipleItem';
import { scanFunction } from '@/models/ScanFunctions';
import { Voortgang } from '@/models/Voortgang';


export const useScanCommonStore = defineStore(`scanCommon`, {
    state: () => ({
        active: new Array<[string, string]>()
    })
})

export const useScanStore = (positie: string, station: string) => {
    const store = defineStore(`scan/${positie}/${station}`, {
        //Set store state
        state: () => ({
            VLDate: '',
            localDate: new Date(),
            scanItems: [] as ScanItem[],
            scanBuffer: [] as string[],
            scanBufferTimeout: null as number | null,
            scanCodesCurrent: [] as string[],
            errorItems: [] as ScanItem[],
            lastNotOkItem: '',
            lastScannedItem: {} as ScanItem,
            scanCounters: [] as ScanCounter[],
            voortgang: [] as Voortgang[],
            scanBundels: [] as ScanBundel[],
            offlineScanItemsQueue: [] as string[],

            //Scan position, station and overview
            scanPosition: {} as ScanPosition,
            scanStation: {} as ScanStation,
            scanOverzicht: {} as ScanOverzicht,
            overviewActive: "" as string,
            scanFunction: null as scanFunction | null,
            lastScannedForFunction: null as string | null,

            //Scan opties
            KlantAfdeling: null as number | null,
            Planbordkaartje: null as number | null,
            PlanbordkaartjeKlant: null as number | null,
            PlanbordkaartjeKlantnaam: null as string | null,
            PlanbordkaartjeAfdelingnaam: null as string | null,
            PlanbordkaartjeRoute: null as number | null,
            Routeweek: null as string | null,

            isReuploading: false,

            currentScan: Promise.resolve()
        }),

        //Set store actions
        actions: {
            //Get current time from server
            async getVLDate() {

                //Check if there was already an VLDate from a previous scan session
                if (this.VLDate || !(await Network.getStatus()).connected) {
                    return;
                }

                try {
                    const response = await VLApi.get(`${useWebportalStore().vlApiUrl}/DateVL`);
                    this.VLDate = response.data.data
                } catch (error: any) {
                    return error.response;
                }
            },

            //Scan method to call from USE THIS METHOD IN COMPONENT
            async scan(code: string, buffer = false) {
                if (this.scanFunction != null) {
                    this.lastScannedForFunction = code
                    return;
                }

                if (buffer) {
                    // Check for duplicate code in buffer
                    if (this.scanBuffer.find(v => v == code)) {
                        return;
                    }
                    
                    //Check for duplicate code in current codes being scanned
                    if (this.scanCodesCurrent.find(v => v == code)) {
                        return;
                    }

                    if (!this.scanPosition.dubbeleScan) {
                        //Check for duplicate code in scanItems
                        this.scanItems.forEach((item) => {
                            if (item.electronicProductCode == code) {
                                return;
                            }
                        })
                    }

                    //Check for duplicate code in notFoundItems
                    this.errorItems.forEach((errorCode) => {
                        if (errorCode.electronicProductCode == code) {
                            return;
                        }
                    })

                    this.scanBuffer.unshift(code);
                    if (this.scanBufferTimeout) {
                        window.clearTimeout(this.scanBufferTimeout)
                    }

                    const feedbackStore = useFeedbackStore();

                    this.scanBufferTimeout = window.setTimeout(async () => {
                        // wait for the current/last scan to complete before starting a new scan
                        await Promise.allSettled([this.currentScan])

                        const codes = this.scanBuffer.slice();
                        this.scanBuffer.splice(0, this.scanBuffer.length);
                        if (codes.length > 0) {
                            feedbackStore.startLoading();
                            this.scanCodesCurrent = codes;
                            this.currentScan = this.getScannedItems(false);
                        }
                    }, 1000);
                } else {
                    this.scanCodesCurrent = [code];
                    await this.getScannedItems(false);
                }
            },

            //Get data by electronic product code and store in scanItems list
            async getScannedItems(isOfflineReupload: boolean) {
                const feedbackStore = useFeedbackStore();
                try {
                    //Check if user is offline and code should be cached
                    if (!(await Network.getStatus()).connected) {
                        this.scanCodesCurrent.forEach(code => {
                            //Check for duplicate code in offlineQueue
                            let duplicatedOfflineCode = false;

                            //If dubbeleScan is enabled don't check for existing code in offlineQueue
                            if (!this.scanPosition.dubbeleScan) {
                                this.offlineScanItemsQueue.forEach((cachedCode) => {
                                    if (cachedCode == code) {
                                        duplicatedOfflineCode = true;
                                        return;
                                    }
                                })
                            }

                            //Cancel function if duplicatedOfflineCode is found
                            if (duplicatedOfflineCode) {
                                return;
                            }

                            //No duplicate found, add code to offlineQueue and give user warning about being offline
                            this.offlineScanItemsQueue.unshift(code);
                            this.lastScannedItem = { electronicProductCode: code } as ScanItem
                            useFeedbackStore().setTranslatedMessage('warning', 'scanCached');
                        });
                        feedbackStore.stopLoading();
                        return;
                    }

                    const scanCodes = this.scanCodesCurrent.filter(code => {
                        let duplicatedCode = false;
                        //If dubbeleScan is enabled don't check for existing code in scanItems
                        if (!this.scanPosition.dubbeleScan) {
                            //Check for duplicate code in scanItems
                            this.scanItems.forEach((item) => {
                                if (item.electronicProductCode == code) {
                                    duplicatedCode = true;
                                    return false;
                                }
                            })
                        }

                        //Check for duplicate code in notFoundItems
                        this.errorItems.forEach((errorCode) => {
                            if (errorCode.electronicProductCode == code) {
                                duplicatedCode = true;
                                return false;
                            }
                        })

                        //Cancel function if duplicatedCode is found
                        if (duplicatedCode) {
                            return false;
                        }

                        return true
                    });

                    // scan in groups of 500 to prevent time outs;
                    const scanWindowSliceSize = 500
                    for (let i = 0; i < Math.ceil(scanCodes.length / scanWindowSliceSize); i++) {
                        const scanCodesSlice = scanCodes.slice(i * scanWindowSliceSize, (i + 1) * scanWindowSliceSize)
                        const params = {
                            api: 2,
                            code: scanCodesSlice,
                            id: "",
                            klantAfdeling: this.KlantAfdeling,
                            klantAfdelingFilter: "",
                            klantControle: (this.KlantAfdeling != null ? true : false),
                            klantFilter: "",
                            koppelDrager: null,
                            ontlaadStang: null,
                            pakbon: 0,
                            positie: this.scanPosition.scanpositie,
                            scan: this.scanPosition.scanUitvoeren,
                            scanHandeling: "",
                            ScanStationId: this.scanStation.id,
                            sorteerUnit: this.scanPosition.sorteerunit,
                            vak: null,
                            planbordkaartje: this.Planbordkaartje,
                            PlanbordkaartjeKlant: this.PlanbordkaartjeKlant,
                            Routeweek: this.Routeweek 
                        }
                        await VLApi.post(`${useWebportalStore().vlApiUrl}/scannings/scan/multiple`, params)
                            .then((response) => {
                                this.processScanResults(scanCodesSlice, response);
                            });
                    }

                    if (!isOfflineReupload) {
                        await this.getScanCounters();
                        await this.getVoortgang(scanCodes[(scanCodes.length - 1)]);
                        await this.getScanBundels();
                    }
                } finally {
                    feedbackStore.stopLoading();
                    this.currentScan = Promise.resolve();
                }
            },

            processScanResults(scanCodesSlice: string[], response: any) {
                const responses = response.data as ScanMultipleItem[]
                scanCodesSlice.forEach((code) => {
                    const data = responses.find(m => m.requestCode === code)
                    if (data) {
                        data.scanResponse.electronicProductCode = code;
                        if (!data.error) {
                            data.scanResponse.status = 'Ok';
                            this.scanItems.unshift(data.scanResponse);
                            this.lastScannedItem = data.scanResponse;
                        } else {
                            //If scan results in error
                            const item = { electronicProductCode: code, status: data.error.message } as ScanItem;

                            //Add sein to status if seined
                            if (data.error.data != null && data.error.data.Sein != '') {
                                item.status = item.status + ': ' + data.scanResponse.sein
                            }

                            this.errorItems.unshift(item);
                            this.lastScannedItem = item;
                            this.lastNotOkItem = item.status;
                        }
                    }
                })
            },

            //Get current scanCounters
            async getScanCounters() {
                const params = {
                    scanPositie: this.scanPosition.scanpositie,
                    scanStation: this.scanStation.scanStation,
                    filterDateTime: this.VLDate
                }
                try {
                    const response = await VLApi.post(`${useWebportalStore().vlApiUrl}/scannings/scancounters`, params);
                    this.scanCounters = response.data.data as ScanCounter[];
                } catch (error: any) {
                    return error.response;
                }
            },
            //Get current voortgang
            async getVoortgang(scancode: string) {
                const params = {
                    scanpositie: this.scanPosition.scanpositie,
                    scancode: scancode
                }
                try {
                    const response = await VLApi.post(`${useWebportalStore().vlApiUrl}/scannings/voortgang`, params);
                    this.voortgang = response.data.data as Voortgang[];
                } catch (error: any) {
                    return error.response;
                }
            },
            //Get current scanBundels
            async getScanBundels() {
                const params = {
                    scanPositie: this.scanPosition.scanpositie,
                    scanStation: this.scanStation.scanStation,
                    filterDateTime: this.VLDate
                }
                try {
                    const response = await VLApi.post(`${useWebportalStore().vlApiUrl}/scannings/scanbundels`, params);
                    this.scanBundels = response.data.data as ScanBundel[];
                } catch (error: any) {
                    return error.response;
                }
            },

            //Retry to upload the cached scanned codes in the offline queue 
            async uploadOfflineQueue() {
                const feedbackStore = useFeedbackStore();
                if (this.offlineScanItemsQueue.length > 0) {
                    try {
                        feedbackStore.startLoading();
                        this.scanCodesCurrent = this.offlineScanItemsQueue;
                        await this.getScannedItems(true);
                        feedbackStore.stopLoading();

                        this.offlineScanItemsQueue = [];
                        this.getScanCounters();
                        await this.getVoortgang(this.offlineScanItemsQueue[(this.offlineScanItemsQueue.length - 1)]);
                        this.getScanBundels();
                    } catch (error: any) {
                        feedbackStore.setTranslatedMessage('danger', 'basicError')
                    }
                }
            },

            //Set VLDate according to the localDate
            async getVLDateAfterOffline() {

                //Check if there was already an VLDate
                if (this.VLDate) {
                    return;
                }

                //Calculate the difference between localDate and currentDate to retract from the VLDate
                const currentDate = new Date();
                const difference = Math.abs(this.localDate.getTime() - currentDate.getTime());

                //Get current VLDate from api and subtract the difference from the VLDate
                await this.getVLDate();
                const _VLDate = new Date(this.VLDate);
                const date = new Date(_VLDate.getTime() - difference);

                //Convert date into right format
                const year = date.getFullYear();
                const month = (date.getMonth() + 1).toString().padStart(2, "0");
                const day = date.getDate().toString().padStart(2, "0");
                const hours = date.getHours().toString().padStart(2, "0");
                const minutes = date.getMinutes().toString().padStart(2, "0");
                const seconds = date.getSeconds().toString().padStart(2, "0");
                const milliseconds = date.getMilliseconds().toString().padStart(3, "0");

                const isoDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
                this.VLDate = isoDate;
            },

            //Reset everything in scanStore to default, except position and station
            reset() {
                this.scanItems = [];
                this.scanCounters = [];
                this.voortgang = [];
                this.errorItems = [];
                this.offlineScanItemsQueue = [];
                this.VLDate = '';
                this.lastNotOkItem = '';
                this.lastScannedItem = {} as ScanItem;
                this.localDate = new Date();
                this.getVLDate();
            },

            //Get scan position and save in state
            async GetScanPosition(position: number) {
                try {
                    const response = await VLApi.get(`${useWebportalStore().vlApiUrl}/scanpositions/${position}`);
                    this.scanPosition = response.data.data as ScanPosition;
                    this.scanFunction = this.scanfunctions.length == 1 ? this.scanfunctions[0] : null
                } catch (error: any) {
                    return error.response;
                }
            },

            //Get scan station and save in state SHOULD ONLY BE CALLED AFTER GetScanPosition HAS BEEN CALLED!
            async GetScanStation(station?: number) {
                const feedbackStore = useFeedbackStore();

                if (station) {
                    try {
                        const response = await VLApi.get(`${useWebportalStore().vlApiUrl}/scanStations/by-positie/${this.scanPosition.scanpositie}?scanstation=${station}`);
                        this.scanStation = response.data.data as ScanStation;
                    } catch (error: any) {
                        feedbackStore.setTranslatedMessage('danger', 'unknownScanStation');
                        return error.response;
                    }
                }
                else {
                    try {
                        const response = await VLApi.get(`${useWebportalStore().vlApiUrl}/scanStations/by-positie/${this.scanPosition.scanpositie}`);
                        this.scanStation = response.data.data as ScanStation;
                    } catch (error: any) {
                        return error.response;
                    }
                }

                if (this.scanStation.scanOverzicht) {
                    this.overviewActive = this.scanStation.scanOverzicht ? "active" : ""
                    this.GetScanOverview(this.scanPosition.scanpositie, this.scanStation.scanStation);
                }
            },

            //voortgang van de scanpositie/barcode.
            // eslint-disable-next-line
            async GetScanPositionVoortgang(_scanpositie: number, _barcode: string) {
                const url = `${useWebportalStore().vlApiUrl}/scannings/scanpositionvoortgang/${this.scanPosition.scanpositie}`;
                try {
                    const response = await VLApi.get(url);
                    return response.data.data;
                } catch (error: any) {
                    return error.response;
                }
            },

            //Will be called automatically when scanOverzicht is set to true
            async GetScanOverview(position: number, station?: number) {
                const url = `${useWebportalStore().vlApiUrl}/scanStations/scanoverview/${position}`;
                const params: any = {
                    Columns: 4
                };

                if (station !== undefined) {
                    params.scanstation = station;
                }

                try {
                    const response = await VLApi.get(url, { params });

                    let counterIndex11 = 1;
                    let counterIndex15 = 1;
                    let counterIndex16 = 1;
                    let counterIndex17 = 1;
                    let counterIndex18 = 1;
                    let counterIndex19 = 1;

                    this.scanOverzicht = response.data.data as ScanOverzicht;
                    //loop through this.scanOverzicht and find the type 11, 15, 16, 17, 18, 19 blok in the rows
                    this.scanOverzicht.rows.forEach((row) => {
                        row.blokken.forEach((blok) => {
                            switch (blok.type) {
                                case 11:
                                    blok.CounterIndex = counterIndex11;
                                    counterIndex11++;
                                    break;
                                case 15:
                                    blok.CounterIndex = counterIndex15;
                                    counterIndex15++;
                                    break;
                                case 16:
                                    blok.CounterIndex = counterIndex16;
                                    counterIndex16++;
                                    break;
                                case 17:
                                    blok.CounterIndex = counterIndex17;
                                    counterIndex17++;
                                    break;
                                case 18:
                                    blok.CounterIndex = counterIndex18;
                                    counterIndex18++;
                                    break;
                                case 19:
                                    blok.CounterIndex = counterIndex19;
                                    counterIndex19++;
                                    break;
                            }
                        })
                    })
                } catch (error: any) {
                    return error.response;
                }
            },

            setKlant(KlantAfdeling: number, Planbordkaartje: number, PlanbordkaartjeKlant: number, Routeweek: string, PlanbordkaartjeKlantnaam: string, PlanbordkaartjeAfdelingnaam: string, PlanbordkaartjeRoute: number) {
                this.KlantAfdeling = KlantAfdeling;
                this.Planbordkaartje = Planbordkaartje;
                this.PlanbordkaartjeKlant = PlanbordkaartjeKlant;
                this.Routeweek = Routeweek;
                this.PlanbordkaartjeKlantnaam = PlanbordkaartjeKlantnaam;
                this.PlanbordkaartjeAfdelingnaam = PlanbordkaartjeAfdelingnaam;
                this.PlanbordkaartjeRoute = PlanbordkaartjeRoute;
            },

            resetKlant() {
                this.KlantAfdeling = null;
                this.Planbordkaartje = null;
                this.PlanbordkaartjeKlant = null;
                this.Routeweek = null;
                this.PlanbordkaartjeKlantnaam = null;
                this.PlanbordkaartjeAfdelingnaam = null;
                this.PlanbordkaartjeRoute = null;
            }
        },

        getters: {
            //Return list of Aantal descending, filtered by types
            highestCounterListByTypes: (state) => (types: number[]) => {
                const filteredCounters = state.scanCounters.filter((counter) => types.includes(counter.type));
                return filteredCounters.length > 0 ? filteredCounters.sort((a, b) => b.aantal - a.aantal) : [];
            },
            scanfunctions: (state) => {
                const scanFunctions: Array<scanFunction> = []
                if (state.scanPosition.barcodeInfo) {
                    scanFunctions.push('barcodeInfo')
                }
                // if (state.scanPosition.barcodeWijzigbaar) {
                //   scanFunctions.push('barcodeWijzigbaar')
                // }
                return scanFunctions
            },
            ScanVoortgangByIndex: (state) => (index: number) => {
                // Return the item at the specified index in the voortgang array, or an empty object if it does not exist
                return state.voortgang ? state.voortgang[index] || {} : {};
            },
        }
    })(); 
    if (useScanCommonStore().active.findIndex(i => i[0] === positie && i[1] === station) === -1) {
        useScanCommonStore().active.push([positie, station]); 
    }
    return store;
};